import { useEffect, useRef } from "react";

import {
  Dialog,
  DialogTitle as HeadlessDialogTitle,
  DialogPanel as HeadlessDialogPanel,
  DialogBackdrop as HeadlessDialogBackdrop,
  CloseButton,
} from "@headlessui/react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

import { FormButton } from "@/components/form/FormButton";
import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

import type {
  DialogProps,
  DialogBackdropProps,
  DialogPanelProps,
  DialogTitleProps,
  CloseButtonProps,
} from "@headlessui/react";

type StringClassNameProps<T> = { className?: string } & Omit<T, "className">;

function useRouteChange(cb: () => void) {
  const pathname = usePathname();
  const ref = useRef(pathname);

  useEffect(() => {
    if (ref.current !== pathname) {
      ref.current = pathname;
      cb();
    }
  }, [pathname, cb]);
}

function DialogRoot({ className, open, onClose, ...rest }: StringClassNameProps<DialogProps>) {
  useRouteChange(() => {
    if (open) {
      onClose?.(false);
    }
  });

  return (
    <Dialog
      className={twMerge("relative z-100 flex", className)}
      open={open}
      onClose={onClose}
      {...rest}
    />
  );
}

function DialogBackdrop({ className, ...rest }: StringClassNameProps<DialogBackdropProps>) {
  return (
    <HeadlessDialogBackdrop className={twMerge("fixed inset-0 bg-black/50", className)} {...rest} />
  );
}

function DialogContainer({ className, ...rest }: React.ComponentProps<"div">) {
  return (
    <div
      className={twMerge("fixed inset-0 flex items-center justify-center", className)}
      {...rest}
    />
  );
}

function DialogPanel({ className, ...rest }: StringClassNameProps<DialogPanelProps>) {
  return (
    <HeadlessDialogPanel
      className={twMerge(
        "relative rounded-12 p-5",
        "bg-document-bg dark:bg-gray-800",
        "flex flex-col",
        "sm:overflow-y-auto",
        className,
      )}
      {...rest}
    />
  );
}

function DialogTitle({ className, ...rest }: StringClassNameProps<DialogTitleProps>) {
  return (
    <HeadlessDialogTitle
      className={twMerge("text-[17px] leading-[21px] font-semibold tracking-tight mr-8", className)}
      {...rest}
    />
  );
}

function DialogSubtitle({
  title,
  children,
  className,
}: {
  title?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <h3
      className={twMerge(
        "text-[12px] leading-[14px] font-mono font-normal tracking-tight uppercase",
        className,
      )}
    >
      {children || title}
    </h3>
  );
}

function DialogCloseButton({ className, ...rest }: StringClassNameProps<CloseButtonProps>) {
  const t = useTranslations();
  return (
    <CloseButton
      aria-label={t("dialog.close")}
      className={twMerge(
        "absolute top-[5px] right-[5px] w-[45px] h-[45px] flex rounded-12",
        "hover:bg-sidebar-button-hover-bg",
        className,
      )}
      {...rest}
    />
  );
}

function DialogCloseIcon({ className, ...rest }: Omit<React.ComponentProps<typeof Icon>, "name">) {
  return (
    <Icon
      name="Cross"
      className={twMerge("relative m-auto w-[28px] h-[28px]", className)}
      {...rest}
    />
  );
}

function DialogFooter({ className, ...rest }: React.ComponentProps<"div">) {
  return <div className={twMerge("flex flex-row mt-4 gap-2", className)} {...rest} />;
}

function DialogButton(props: React.ComponentProps<typeof FormButton>) {
  return <FormButton size="sm" {...props} />;
}

export {
  DialogRoot as Root,
  DialogBackdrop as Backdrop,
  DialogContainer as Container,
  DialogPanel as Panel,
  DialogTitle as Title,
  DialogSubtitle as Subtitle,
  DialogCloseButton as CloseButton,
  DialogCloseIcon as CloseIcon,
  DialogFooter as Footer,
  DialogButton as Button,
};
